import { Link } from "react-router-dom";
import { ArrowRight, Users, AlertTriangle, ListChecks, TrendingUp } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import ServiceIcon from "@/components/ServiceIcon";
import { CATEGORIES, SERVICES } from "@/data/site";
import { SERVICE_OPTIONS } from "@/config/company";

function enquiryOption(s) {
  if (s.id === "vapt") return "Vulnerability Assessment and Penetration Testing";
  return SERVICE_OPTIONS.includes(s.shortName) ? s.shortName : "General Enquiry";
}

function ServiceBlock({ service, index }) {
  return (
    <article id={service.id} className="scroll-mt-28 py-14 sm:py-16 border-b border-slate-200 last:border-0" data-testid={`service-detail-${service.id}`}>
      <Reveal>
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-navy flex items-center justify-center text-teal shrink-0">
                <ServiceIcon name={service.icon} size={26} />
              </div>
              <span className="font-mono text-xs text-muted" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
            </div>
            <h3 className="mt-5 font-heading text-xl sm:text-2xl font-bold tracking-tight text-navy">{service.name}</h3>
            <p className="mt-4 text-base leading-relaxed text-muted">{service.summary}</p>
            <div className="mt-6 rounded-xl bg-mist border border-slate-200 p-5">
              <h4 className="flex items-center gap-2 text-sm font-semibold text-ink">
                <Users size={15} className="text-corp" aria-hidden="true" /> Who it is for
              </h4>
              <p className="mt-2 text-sm leading-relaxed text-muted">{service.audience}</p>
            </div>
            <Link
              to={`/contact?service=${encodeURIComponent(enquiryOption(service))}`}
              data-testid={`discuss-service-${service.id}`}
              className="mt-6 inline-flex items-center gap-2 bg-corp hover:bg-bright text-white text-sm font-semibold px-6 py-3 rounded-full transition-colors duration-200"
            >
              Discuss This Service <ArrowRight size={15} aria-hidden="true" />
            </Link>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-6">
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <h4 className="flex items-center gap-2 text-sm font-semibold text-ink mb-4">
                <ListChecks size={15} className="text-teal-dark" aria-hidden="true" /> What the service covers
              </h4>
              <ul className="space-y-2.5">
                {service.covers.map((c, i) => (
                  <li key={i} className="flex gap-2.5 text-sm text-muted leading-snug">
                    <span className="w-1 h-1 rounded-full bg-teal mt-2 shrink-0" aria-hidden="true" />{c}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <h4 className="flex items-center gap-2 text-sm font-semibold text-ink mb-4">
                <AlertTriangle size={15} className="text-corp" aria-hidden="true" /> Challenges addressed
              </h4>
              <ul className="space-y-2.5">
                {service.challenges.map((c, i) => (
                  <li key={i} className="flex gap-2.5 text-sm text-muted leading-snug">
                    <span className="w-1 h-1 rounded-full bg-corp mt-2 shrink-0" aria-hidden="true" />{c}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <h4 className="flex items-center gap-2 text-sm font-semibold text-ink mb-4">
                <ListChecks size={15} className="text-corp" aria-hidden="true" /> Key activities
              </h4>
              <ul className="space-y-2.5">
                {service.activities.map((c, i) => (
                  <li key={i} className="flex gap-2.5 text-sm text-muted leading-snug">
                    <span className="w-1 h-1 rounded-full bg-bright mt-2 shrink-0" aria-hidden="true" />{c}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl border border-teal/25 bg-teal/5 p-6">
              <h4 className="flex items-center gap-2 text-sm font-semibold text-ink mb-4">
                <TrendingUp size={15} className="text-teal-dark" aria-hidden="true" /> Expected benefits
              </h4>
              <ul className="space-y-2.5">
                {service.benefits.map((c, i) => (
                  <li key={i} className="flex gap-2.5 text-sm text-ink/80 leading-snug">
                    <span className="w-1 h-1 rounded-full bg-teal-dark mt-2 shrink-0" aria-hidden="true" />{c}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        {service.note && (
          <p className="mt-8 text-xs leading-relaxed text-muted italic border-l-2 border-teal/50 pl-4 max-w-3xl">
            {service.note}
          </p>
        )}
      </Reveal>
    </article>
  );
}

export default function Services() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: SERVICES.map((s, i) => ({
      "@type": "Service",
      position: i + 1,
      name: s.name,
      description: s.summary,
      provider: { "@type": "ProfessionalService", name: "Numeric Consulting & Solutions" },
    })),
  };
  return (
    <>
      <SEO
        title="Services"
        description="ISO 27001, ISO 22301, ISO 9001 and ISO 38500 consulting, PCI DSS readiness, cybersecurity assessments, SWIFT CSP assessments, VAPT, ISO standards training, IT consulting and application development."
        path="/services"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Services", path: "/services" }]}
        schema={schema}
      />
      <PageHero
        eyebrow="Our Services"
        title="Consulting Services Across Standards, Security and Technology"
        description="Four integrated practice areas — management systems and ISO standards, cybersecurity and technical assurance, payment security, and technology consulting — delivered with one consistent standard of rigour."
        breadcrumb="Services"
      />

      {/* Category navigation */}
      <nav aria-label="Service categories" className="sticky top-[60px] sm:top-[68px] z-30 bg-white/95 backdrop-blur border-b border-slate-200" data-testid="service-category-nav">
        <div className="container-site flex gap-2 overflow-x-auto py-3.5">
          {CATEGORIES.map((c) => (
            <a
              key={c.id}
              href={`#cat-${c.id}`}
              data-testid={`category-nav-${c.id}`}
              className="shrink-0 rounded-full border border-slate-200 bg-mist px-4 py-2 text-xs sm:text-sm font-semibold text-ink hover:border-corp/40 hover:text-corp transition-colors"
            >
              {c.label}
            </a>
          ))}
        </div>
      </nav>

      <div className="bg-white">
        {CATEGORIES.map((cat) => {
          const services = SERVICES.filter((s) => s.category === cat.id);
          return (
            <section key={cat.id} id={`cat-${cat.id}`} className="scroll-mt-32" data-testid={`category-section-${cat.id}`}>
              <div className="container-site">
                <div className="pt-14 sm:pt-16">
                  <Reveal>
                    <p className="eyebrow mb-3">Practice Area</p>
                    <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-navy">{cat.label}</h2>
                  </Reveal>
                </div>
                <div>
                  {services.map((s, i) => (
                    <ServiceBlock key={s.id} service={s} index={SERVICES.indexOf(s)} />
                  ))}
                </div>
              </div>
            </section>
          );
        })}
      </div>

      <CTASection
        title="Not Sure Which Service Fits Your Situation?"
        description="Describe your objectives and challenges — we will help you identify the right starting point, scope and sequence."
      />
    </>
  );
}
